import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Play, Pause, Microphone } from '@phosphor-icons/react';

// Module-level tracker: only ONE voice message plays at a time across the app.
let _currentPlayingAudio = null;
let _currentPlayingSetter = null;

// Deterministic pseudo-random 0..1 based on a string seed (so waveform stays stable per URL)
const seedFrom = (s) => {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return () => {
    h ^= h << 13; h >>>= 0;
    h ^= h >> 17; h >>>= 0;
    h ^= h << 5;  h >>>= 0;
    return (h & 0xffffffff) / 0xffffffff;
  };
};

const BAR_COUNT = 34;

const VoiceMessage = ({ audioUrl, isSender = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [heard, setHeard] = useState(false);
  const audioRef = useRef(null);

  // Deterministic waveform bars (stays stable per URL, no re-random on re-render)
  const bars = useMemo(() => {
    const rand = seedFrom(String(audioUrl || 'default'));
    return Array.from({ length: BAR_COUNT }, () => 0.25 + rand() * 0.75);
  }, [audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      // Some browsers report Infinity for webm — best-effort fix
      const d = isFinite(audio.duration) ? audio.duration : 0;
      setDuration(d);
    };
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      setHeard(true);
      if (_currentPlayingAudio === audio) {
        _currentPlayingAudio = null;
        _currentPlayingSetter = null;
      }
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed]);

  const togglePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    try {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        if (audio.readyState < 2) {
          await new Promise((resolve, reject) => {
            const t = setTimeout(() => reject(new Error('timeout')), 10000);
            audio.addEventListener('canplay', () => { clearTimeout(t); resolve(); }, { once: true });
            audio.addEventListener('error', () => { clearTimeout(t); reject(audio.error); }, { once: true });
            audio.load();
          });
        }
        if (_currentPlayingAudio && _currentPlayingAudio !== audio) {
          try { _currentPlayingAudio.pause(); } catch (_) {}
          if (_currentPlayingSetter) _currentPlayingSetter(false);
        }
        _currentPlayingAudio = audio;
        _currentPlayingSetter = setIsPlaying;
        audio.playbackRate = speed;
        await audio.play();
        setIsPlaying(true);
        setHeard(true);
      }
    } catch (err) {
      setIsPlaying(false);
    }
  };

  const cycleSpeed = (e) => {
    e.stopPropagation();
    setSpeed(prev => (prev === 1 ? 1.5 : prev === 1.5 ? 2 : 1));
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const displayTime = isPlaying || currentTime > 0 ? currentTime : duration;

  // WhatsApp palette
  const playedColor = isSender ? '#54656f' : '#54656f';
  const unplayedColor = isSender ? 'rgba(9, 60, 78, 0.35)' : 'rgba(9, 60, 78, 0.28)';
  const iconBg = isSender ? '#0b8f70' : '#0b8f70';

  return (
    <div
      className="flex items-center gap-2 py-1.5 pl-1 pr-2 min-w-[230px] max-w-[300px] select-none"
      data-testid="voice-message"
    >
      {/* Avatar / Mic circle */}
      <div className="relative flex-shrink-0">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: '#dfe5e7' }}
        >
          <Microphone size={20} weight="fill" style={{ color: heard ? '#8696a0' : '#00a884' }} />
        </div>
        {/* Unheard blue dot (only for receiver, before first play) */}
        {!isSender && !heard && (
          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#00a884] border-2 border-white" />
        )}
      </div>

      {/* Play / Pause */}
      <button
        onClick={togglePlayPause}
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-transform active:scale-95"
        style={{ background: 'transparent' }}
        data-testid="voice-play-btn"
        aria-label={isPlaying ? 'Pause voice message' : 'Play voice message'}
      >
        {isPlaying ? (
          <Pause size={22} weight="fill" style={{ color: iconBg }} />
        ) : (
          <Play size={22} weight="fill" style={{ color: iconBg }} />
        )}
      </button>

      {/* Waveform + Meta */}
      <div className="flex-1 flex flex-col justify-center min-w-0">
        {/* Waveform bars — deterministic */}
        <div className="relative flex items-center gap-[2px] h-6" data-testid="voice-waveform">
          {bars.map((h, i) => {
            const isPast = (i / bars.length) * 100 <= progress;
            return (
              <div
                key={i}
                className="flex-1 rounded-full"
                style={{
                  height: `${Math.max(10, h * 100)}%`,
                  minWidth: '2px',
                  maxWidth: '3px',
                  background: isPast ? playedColor : unplayedColor,
                  transition: 'background-color 80ms linear',
                }}
              />
            );
          })}
          {/* Progress dot */}
          <div
            className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full"
            style={{
              left: `calc(${progress}% - 5px)`,
              background: '#00a884',
              boxShadow: '0 0 0 2px rgba(255,255,255,0.9)',
              transition: 'left 80ms linear',
            }}
          />
        </div>

        {/* Meta row: time + speed indicator */}
        <div className="flex items-center justify-between mt-[3px]">
          <span className="text-[11px] text-[#667781] font-mono leading-none">
            {formatTime(displayTime)}
          </span>
          {(isPlaying || speed !== 1) && (
            <button
              onClick={cycleSpeed}
              className="text-[10px] font-bold px-1.5 py-[1px] rounded-full text-white leading-none"
              style={{ background: '#8696a0' }}
              data-testid="voice-speed-btn"
              title="Playback speed"
            >
              {speed}x
            </button>
          )}
        </div>
      </div>

      {/* Hidden audio element */}
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
    </div>
  );
};

export default VoiceMessage;
